import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Search,
  Calendar,
  MapPin,
  Users,
  Trophy,
  Plus,
  Filter,
  Clock,
  DollarSign,
  Loader2,
  Heart,
  Flag,
  Medal,
  PartyPopper,
  Handshake,
  Dog
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SportsEvents = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'all');
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState({ competitive: [], non_competitive: [] });
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRegistrations, setMyRegistrations] = useState([]);
  
  // Filters
  const [categoryFilter, setCategoryFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Create event dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    category: 'meetup',
    event_type: 'non_competitive',
    venue_name: '',
    address: '',
    city: '',
    state: '',
    start_date: '',
    start_time: '',
    max_participants: '',
    entry_fee: '',
    pet_types_allowed: ['dog'],
    accepting_sponsors: false
  });
  const [saving, setSaving] = useState(false);
  
  // Registration dialog
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [userPets, setUserPets] = useState([]);
  const [selectedPetId, setSelectedPetId] = useState('');
  
  // Leaderboard filters
  const [leaderboardCategory, setLeaderboardCategory] = useState('agility');
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('all_time');

  useEffect(() => {
    fetchCategories();
    fetchEvents();
    if (user) {
      fetchMyRegistrations();
      fetchUserPets();
    }
  }, [user]);

  useEffect(() => {
    fetchEvents();
  }, [categoryFilter, eventTypeFilter, activeTab]);

  useEffect(() => {
    if (activeTab === 'leaderboards') {
      fetchLeaderboard();
    }
  }, [activeTab, leaderboardCategory, leaderboardPeriod]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/sports-events/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryFilter) params.append('category', categoryFilter);
      if (eventTypeFilter) params.append('event_type', eventTypeFilter);
      if (cityFilter) params.append('city', cityFilter);
      
      // Tab-specific filters
      if (activeTab === 'competitive') params.append('event_type', 'competitive');
      if (activeTab === 'gatherings') params.append('event_type', 'non_competitive');
      
      const response = await axios.get(`${API}/sports-events?${params}`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyRegistrations = async () => {
    try {
      const response = await axios.get(`${API}/sports-events/my/registrations`);
      setMyRegistrations(response.data);
    } catch (error) {
      console.error('Error fetching registrations:', error);
    }
  };

  const fetchUserPets = async () => {
    try {
      const response = await axios.get(`${API}/pets/me`);
      setUserPets(response.data);
    } catch (error) {
      console.error('Error fetching pets:', error);
    }
  };

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/sports-events/leaderboard/${leaderboardCategory}?time_period=${leaderboardPeriod}`
      );
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.venue_name || !newEvent.start_date) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      const eventData = {
        ...newEvent,
        max_participants: newEvent.max_participants ? parseInt(newEvent.max_participants) : null,
        entry_fee: newEvent.entry_fee ? parseFloat(newEvent.entry_fee) : null
      };
      
      await axios.post(`${API}/sports-events`, eventData);
      toast.success('Event created successfully!');
      setCreateDialogOpen(false);
      fetchEvents();
      resetNewEvent();
    } catch (error) {
      toast.error('Failed to create event');
    } finally {
      setSaving(false);
    }
  };

  const handleRegister = async () => {
    if (!selectedPetId) {
      toast.error('Please select a pet');
      return;
    }
    
    try {
      await axios.post(`${API}/sports-events/${selectedEvent.id}/register`, {
        event_id: selectedEvent.id,
        pet_id: selectedPetId
      });
      toast.success('Successfully registered!');
      setRegisterDialogOpen(false);
      fetchMyRegistrations();
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register');
    }
  };

  const resetNewEvent = () => {
    setNewEvent({
      title: '',
      description: '',
      category: 'meetup',
      event_type: 'non_competitive',
      venue_name: '',
      address: '',
      city: '',
      state: '',
      start_date: '',
      start_time: '',
      max_participants: '',
      entry_fee: '',
      pet_types_allowed: ['dog'],
      accepting_sponsors: false
    });
  };

  const isRegistered = (eventId) => {
    return myRegistrations.some(r => r.event_id === eventId && r.status !== 'cancelled');
  };

  const getCategoryIcon = (category) => {
    const icons = {
      agility: Trophy,
      obedience: Medal,
      conformation: Medal,
      rally: Flag,
      meetup: Users,
      group_walk: Users,
      convention: PartyPopper,
      training_workshop: Dog,
      social_event: PartyPopper,
      fundraiser: Heart
    };
    return icons[category] || Calendar;
  };

  const getCategoryLabel = (category) => {
    const allCategories = [...categories.competitive, ...categories.non_competitive];
    const found = allCategories.find(c => c.value === category);
    return found?.label || category;
  };

  const filteredEvents = events.filter(event =>
    event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    event.city.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold mb-2 flex items-center gap-3">
            <Trophy className="w-8 h-8 text-primary" />
            Sports & Gatherings
          </h1>
          <p className="text-muted-foreground">
            Competitions, rallies, conventions, meetups, and more
          </p>
        </div>
        
        {user && (
          <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            Create Event
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSearchParams({ tab: v }); }} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
          <TabsTrigger value="all" data-testid="tab-all">
            <Calendar className="w-4 h-4 mr-2" />
            All Events
          </TabsTrigger>
          <TabsTrigger value="competitive" data-testid="tab-competitive">
            <Trophy className="w-4 h-4 mr-2" />
            Competitions
          </TabsTrigger>
          <TabsTrigger value="gatherings" data-testid="tab-gatherings">
            <Users className="w-4 h-4 mr-2" />
            Gatherings
          </TabsTrigger>
          <TabsTrigger value="leaderboards" data-testid="tab-leaderboards">
            <Medal className="w-4 h-4 mr-2" />
            Leaderboards
          </TabsTrigger>
        </TabsList>

        {/* All Events / Competitions / Gatherings Tabs */}
        {['all', 'competitive', 'gatherings'].includes(activeTab) && (
          <TabsContent value={activeTab} className="space-y-6">
            {/* Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search events..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  
                  <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-44">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      <SelectItem value="---" disabled>─ Competitive ─</SelectItem>
                      {categories.competitive.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                      <SelectItem value="----" disabled>─ Gatherings ─</SelectItem>
                      {categories.non_competitive.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Input
                    placeholder="City"
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    className="w-32"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Events Grid */}
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredEvents.map((event) => {
                  const CategoryIcon = getCategoryIcon(event.category);
                  const registered = isRegistered(event.id);
                  
                  return (
                    <Card key={event.id} className="overflow-hidden" data-testid={`event-card-${event.id}`}>
                      {event.image_url && (
                        <div className="aspect-video bg-muted">
                          <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <Badge variant={event.event_type === 'competitive' ? 'default' : 'secondary'} className="mb-2">
                              <CategoryIcon className="w-3 h-3 mr-1" />
                              {getCategoryLabel(event.category)}
                            </Badge>
                            <CardTitle className="text-lg">{event.title}</CardTitle>
                          </div>
                          {registered && (
                            <Badge className="bg-green-500 shrink-0">Registered</Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          {format(new Date(event.start_date), 'MMM d, yyyy')} at {event.start_time}
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <MapPin className="w-4 h-4" />
                          {event.venue_name}, {event.city}, {event.state}
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Users className="w-4 h-4" />
                          {event.current_participants} registered
                          {event.max_participants && ` / ${event.max_participants} max`}
                        </div>
                        {event.entry_fee && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <DollarSign className="w-4 h-4" />
                            ${event.entry_fee} entry fee
                          </div>
                        )}
                        
                        {event.sponsors?.length > 0 && (
                          <div className="flex items-center gap-2">
                            <Handshake className="w-4 h-4 text-primary" />
                            <span className="text-xs">Sponsored by {event.sponsors.length} partner(s)</span>
                          </div>
                        )}
                      </CardContent>
                      <CardFooter className="pt-0">
                        {user && !registered ? (
                          <Button 
                            className="w-full" 
                            onClick={() => { setSelectedEvent(event); setRegisterDialogOpen(true); }}
                            disabled={event.registration_status === 'closed'}
                          >
                            {event.registration_status === 'closed' ? 'Registration Closed' : 'Register'}
                          </Button>
                        ) : !user ? (
                          <Button className="w-full" variant="outline" onClick={() => navigate('/login')}>
                            Log in to Register
                          </Button>
                        ) : (
                          <Button className="w-full" variant="outline" disabled>
                            Already Registered
                          </Button>
                        )}
                      </CardFooter>
                    </Card>
                  );
                })}
              </div>
            )}

            {filteredEvents.length === 0 && !loading && (
              <div className="text-center py-12 text-muted-foreground">
                <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No events found matching your criteria</p>
                {user && (
                  <Button variant="link" onClick={() => setCreateDialogOpen(true)}>
                    Create the first one!
                  </Button>
                )}
              </div>
            )}
          </TabsContent>
        )}

        {/* Leaderboards Tab */}
        <TabsContent value="leaderboards" className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-muted-foreground" />
                  <Select value={leaderboardCategory} onValueChange={setLeaderboardCategory}>
                    <SelectTrigger className="w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.competitive.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Select value={leaderboardPeriod} onValueChange={setLeaderboardPeriod}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_time">All Time</SelectItem>
                    <SelectItem value="yearly">This Year</SelectItem>
                    <SelectItem value="monthly">This Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : leaderboard.length > 0 ? (
            <div className="space-y-4">
              {leaderboard.map((entry, idx) => (
                <Card key={entry.pet_id} className={idx < 3 ? 'border-2 border-primary/30' : ''}>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                        idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                        idx === 1 ? 'bg-gray-300 text-gray-700' :
                        idx === 2 ? 'bg-orange-400 text-orange-900' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        #{entry.rank}
                      </div>
                      
                      {entry.pet_image ? (
                        <img 
                          src={entry.pet_image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${entry.pet_image}` : entry.pet_image}
                          alt={entry.pet_name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <Dog className="w-6 h-6 text-primary" />
                        </div>
                      )}
                      
                      <div className="flex-1">
                        <h3 className="font-semibold">{entry.pet_name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {entry.pet_breed} • Owner: {entry.owner_username}
                        </p>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-2xl font-bold text-primary">{entry.total_points} pts</div>
                        <div className="text-xs text-muted-foreground">
                          {entry.total_wins} wins • {entry.total_events} events
                        </div>
                      </div>
                    </div>
                    
                    {entry.titles_earned.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {entry.titles_earned.map((title) => (
                          <Badge key={`title-${title}`} variant="outline" className="text-xs">
                            <Medal className="w-3 h-3 mr-1" />
                            {title}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No competition results yet for this category</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Event Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Event</DialogTitle>
            <DialogDescription>
              Host a competition, rally, meetup, or gathering
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Event Type *</label>
                <Select 
                  value={newEvent.event_type} 
                  onValueChange={(v) => setNewEvent(prev => ({ ...prev, event_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="competitive">Competition</SelectItem>
                    <SelectItem value="non_competitive">Gathering/Meetup</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Category *</label>
                <Select 
                  value={newEvent.category} 
                  onValueChange={(v) => setNewEvent(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {newEvent.event_type === 'competitive' ? (
                      categories.competitive.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))
                    ) : (
                      categories.non_competitive.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Event Title *</label>
              <Input
                value={newEvent.title}
                onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Spring Agility Championship 2026"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Description</label>
              <Textarea
                value={newEvent.description}
                onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Tell participants what to expect..."
                rows={3}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Venue Name *</label>
                <Input
                  value={newEvent.venue_name}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, venue_name: e.target.value }))}
                  placeholder="e.g., City Dog Park"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Address</label>
                <Input
                  value={newEvent.address}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="123 Main St"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">City *</label>
                <Input
                  value={newEvent.city}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="City"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">State *</label>
                <Input
                  value={newEvent.state}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, state: e.target.value }))}
                  placeholder="State"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Date *</label>
                <Input
                  type="date"
                  value={newEvent.start_date}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Time *</label>
                <Input
                  type="time"
                  value={newEvent.start_time}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, start_time: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Max Participants</label>
                <Input
                  type="number"
                  value={newEvent.max_participants}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, max_participants: e.target.value }))}
                  placeholder="Leave empty for unlimited"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Entry Fee ($)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={newEvent.entry_fee}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, entry_fee: e.target.value }))}
                  placeholder="Leave empty if free"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="accepting_sponsors"
                checked={newEvent.accepting_sponsors}
                onChange={(e) => setNewEvent(prev => ({ ...prev, accepting_sponsors: e.target.checked }))}
                className="rounded"
              />
              <label htmlFor="accepting_sponsors" className="text-sm">
                Accept sponsorship applications
              </label>
            </div>

            <Button onClick={handleCreateEvent} disabled={saving} className="w-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Event
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Registration Dialog */}
      <Dialog open={registerDialogOpen} onOpenChange={setRegisterDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Register for Event</DialogTitle>
            <DialogDescription>
              {selectedEvent?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Select Your Pet</label>
              <Select value={selectedPetId} onValueChange={setSelectedPetId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a pet..." />
                </SelectTrigger>
                <SelectContent>
                  {userPets.map((pet) => (
                    <SelectItem key={pet.id} value={pet.id}>
                      {pet.name} ({pet.breed || pet.pet_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedEvent?.entry_fee && (
              <div className="bg-muted p-3 rounded-lg">
                <p className="text-sm">
                  <strong>Entry Fee:</strong> ${selectedEvent.entry_fee}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Payment will be collected separately
                </p>
              </div>
            )}

            <Button onClick={handleRegister} className="w-full">
              Confirm Registration
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SportsEvents;
